/**
 * The functions in this module are mostly copied from the generated
 * Emscripten PHP module. This enables features like filesystem journaling,
 * which use some low-level Emscripten APIs and need access to the
 * same path helpers.
 */

/**
 * Joins paths together.
 *
 * For example:
 *
 * > joinPaths('wordpress', 'wp-content')
 * 'wordpress/wp-content'
 *
 * Use this for all PHP paths and **do not** use path.join().
 * This is important because Emscripten paths are **always**
 * POSIX-style paths. Imagine joining paths on Windows:
 *
 * > path.join('wordpress', 'wp-content')
 * '\\wordpress\\wp-content'  // invalid in PHP.wasm
 *
 * See the path.join issue for more details:
 *
 * https://github.com/WordPress/playground-tools/issues/11#issuecomment-1579074763
 *
 * @param paths Paths segments to join
 * @returns A joined path
 */
export function joinPaths(...paths: string[]) {
	function hasTrailingSlash(p: string) {
		return p.substring(p.length - 1) === '/';
	}

	let path = paths.join('/');
	const isAbsolute = path[0] === '/';
	const trailingSlash = hasTrailingSlash(path);
	path = normalizePath(path);
	if (!path && !isAbsolute) {
		path = '.';
	}
	if (path && trailingSlash && !hasTrailingSlash(path)) {
		path += '/';
	}
	return path;
}

/**
 * Resolves a path and returns it only when it stays under a parent path.
 *
 * Absolute paths are checked as-is. Relative paths are resolved from the
 * parent path. The parent itself is not considered to be under the parent.
 * Paths containing null bytes are rejected before resolution.
 *
 * @param path The absolute or relative path to resolve.
 * @param parentPath The path the result must stay under.
 * @returns The normalized resolved path, or undefined when it is not a child.
 */
export function resolvePathUnder(path: string, parentPath: string) {
	if (path.includes('\0') || parentPath.includes('\0')) {
		return undefined;
	}
	const normalizedParentPath = normalizePath(parentPath);
	if (!normalizedParentPath) {
		return undefined;
	}
	const resolvedPath = normalizePath(
		path.startsWith('/') ? path : joinPaths(normalizedParentPath, path)
	);
	if (
		resolvedPath === normalizedParentPath ||
		!isParentOf(normalizedParentPath, resolvedPath)
	) {
		return undefined;
	}
	return resolvedPath;
}

/**
 * Returns the directory name of a path.
 *
 * @param path
 * @returns
 */
export function dirname(path: string) {
	if (path === '/') {
		return '/';
	}

	path = normalizePath(path);

	const lastSlash = path.lastIndexOf('/');
	if (lastSlash === -1) {
		return '';
	} else if (lastSlash === 0) {
		return '/';
	}
	return path.substr(0, lastSlash);
}

/**
 * Returns the last portion of a path.
 *
 * @param path - The path to extract the basename from.
 * @returns The basename of the path.
 */
export function basename(path: string) {
	if (path === '/') {
		return '/';
	}

	path = normalizePath(path);

	const lastSlash = path.lastIndexOf('/');
	if (lastSlash === -1) {
		return path;
	}
	return path.substr(lastSlash + 1);
}

/**
 * Normalizes a path.
 *
 * For example:
 *
 * > normalizePath('wordpress/wp-content/../')
 * 'wordpress'
 *
 * @param path
 * @returns
 */
export function normalizePath(path: string) {
	const isAbsolute = path[0] === '/';
	path = normalizePathsArray(
		path.split('/').filter((p: any) => !!p),
		!isAbsolute
	).join('/');
	return (isAbsolute ? '/' : '') + path.replace(/\/$/, '');
}

/**
 * Normalizes paths.
 *
 * For example:
 *
 * > normalizePathsArray(['wordpress', 'wp-content', '..', '', '.',
 * 'wp-includes']) ['wordpress', 'wp-includes']
 *
 * @param parts parts of the path to normalize
 * @param allowAboveRoot allow paths above the root
 * @returns normalized paths
 */
export function normalizePathsArray(parts: string[], allowAboveRoot: boolean) {
	let up = 0;
	for (let i = parts.length - 1; i >= 0; i--) {
		const last = parts[i];
		if (last === '.') {
			parts.splice(i, 1);
		} else if (last === '..') {
			parts.splice(i, 1);
			up++;
		} else if (up) {
			parts.splice(i, 1);
			up--;
		}
	}
	if (allowAboveRoot) {
		for (; up; up--) {
			parts.unshift('..');
		}
	}
	return parts;
}

/**
 * Checks if the given parent path is an ancestor of the given child path.
 *
 * @param parent The parent path to check.
 * @param child The child path to verify against the parent.
 * @returns Whether the `parent` path is an ancestor of the `child` path.
 */
export function isParentOf(parent: string, child: string) {
	if (parent === '/') {
		return true;
	}
	parent = normalizePath(parent);
	child = normalizePath(child);
	return child.startsWith(parent + '/') || child === parent;
}

/**
 * Guarantees a path is absolute by prepending `/` if needed.
 *
 * Useful when working with user-provided paths that might be relative,
 * or when you need to normalize edge cases like empty strings.
 *
 * For example:
 *
 * > ensureAbsolutePath('wp-content/uploads')
 * '/wp-content/uploads'
 *
 * > ensureAbsolutePath('/already/absolute')
 * '/already/absolute'
 *
 * > ensureAbsolutePath('')
 * '/'
 *
 * @param path - The path to make absolute.
 * @returns An absolute, normalized path starting with `/`.
 */
export function ensureAbsolutePath(path: string) {
	return joinPaths('/', normalizePath(path || '/'));
}

/**
 * Converts a native OS path to a POSIX-style path.
 *
 * Transformations:
 * 1. Backslashes → forward slashes
 * 2. Windows drive letter `C:\` → `/C/` (colons are invalid in
 *    Emscripten VFS paths and cause ENOTDIR errno 28)
 *
 * On POSIX systems this is effectively a no-op.
 *
 * @see https://github.com/emscripten-core/emscripten/issues/17829
 */
export function toPosixPath(nativePath: string): string {
	let result = nativePath.replaceAll('\\', '/');
	// Handle Windows drive letter: C:/ → /C/
	const driveMatch = result.match(/^([A-Za-z]):\//);
	if (driveMatch) {
		result = '/' + driveMatch[1] + result.slice(2);
	}
	return result;
}
